import React, { Component } from 'react';
import { Button, Theme, WithStyles, createStyles } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { MyPATDialog } from '../../../common';
import Tooltip from '@material-ui/core/Tooltip';
import { CHARACTER_LIMIT, MAXIMUM_REVIEW_CHARACTERS, OTHER_REASON_ID } from '../../constant/content';
import cx from 'classnames';

const styles = (theme: Theme) => createStyles({
    dialogCustomHeading: {
        fontSize: '1.5rem',
        fontWeight: 600,
        lineHeight: '138%',
        color: '#333333',
        textTransform: 'initial',
    },
    buttonSection: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        '& button': {
            minWidth: '7.25rem',
            minHeight: '44px',
            fontWeight: 400,
            fontSize: '1rem',
        }
    },
    formSection: {
        '& .form-group': {
            marginBottom: '0.94rem',
            '& label': {
                marginBottom: '0.63rem',
                fontSize: '0.88rem',
                fontWeight: 500,
                lineHeight: '136%',
                color: '#666666',
                display: 'flex',
                alignItems: 'center',
                '& .light-text': {
                    color: '#999999',
                },
                '& .info-icon': {
                    marginLeft: '0.19rem',
                    '& svg': {
                        display: 'block',
                    }
                }
            },
            '& textarea, & input': {
                border: '1px solid #ECECEC',
                borderRadius: '0.25rem',
                height: '6.31rem',
                fontSize: '0.88rem',
                fontWeight: 400,
                color: '#333333',
                width: '100%',
                resize: 'none',
                padding: '0.75rem 0.75rem',
                fontFamily: '"Nunito", sans-serif',
                '&::placeholder': {
                    color: '#999999',
                },
                '&:focus': {
                    outline: 'none',
                }
            },
            '& input': {
                height: '2.75rem',
                padding: '0.45rem 0.75rem',
            },
            '& .note-text': {
                fontSize: '0.75rem',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '0.88rem',
                color: '#999999',
                display: 'block',
                textAlign: 'right',
            }
        },
    },
    rejectionList: {
        listStyle: 'none',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '0.63rem 0.5rem',
        paddingLeft: '0px',
        margin: '0px',
        '& .rejection-list-item': {
            border: '1px solid #E6E6E6',
            borderRadius: '1.38rem',
            padding: '0.47rem 0.70rem',
            fontSize: '0.88rem',
            fontWeight: 500,
            color: '#999999',
            position: 'relative',
            '& input': {
                position: 'absolute',
                top: '0',
                left: '0',
                width: '100%',
                height: '100%',
                opacity: 0,
                margin: '0',
                cursor: 'pointer',
            }
        },
        '& .rejection-list-item:has(input:checked)': {
            borderColor: '#1D7DEA',
            backgroundColor: '#1D7DEA',
            color: '#ffffff',
        }
    },
    customTooltipOuter: {
        opacity: 1,
        margin: '20px auto 0',
        '& .custom-tooltip': {
            maxWidth: '307px',
            backgroundColor: 'rgba(244, 249, 255, 1)',
            boxShadow: '0px 2px 7px 0px #00000033',
            position: 'relative',
            borderRadius: '4px',
            padding: '0',
            margin: '0',
            '&::before': {
                content: "''",
                top: '-15px',
                left: '20px',
                width: '0',
                height: '0',
                position: 'absolute',
                borderLeft: '10px solid transparent',
                borderRight: '10px solid transparent',
                borderBottom: '15px solid rgba(244, 249, 255, 1)',
                zIndex: 1,
            },
            '&::after': {
                content: "''",
                top: '-15px',
                left: '20px',
                width: '0',
                height: '0',
                position: 'absolute',
                borderLeft: '10px solid transparent',
                borderRight: '10px solid transparent',
                borderBottom: '15px solid rgba(255, 255, 255, 0.2)',
                transform: 'scale(1.4)',
                zIndex: 0,
                filter: 'drop-shadow(0px -2px 0px rgba(0, 0, 0, 0.2))',
            },
            '& .custom-tooltip-inner': {
                padding: '12px 12px 12px 18px',
                '& .listing': {
                    margin: '0',
                    paddingLeft: '0',
                    listStyle: 'none',
                    '& li': {
                        fontSize: '12px',
                        fontWeight: 400,
                        lineHeight: '18px',
                        color: '#666666',
                        paddingLeft: '12px',
                        position: 'relative',
                        '&::before': {
                            content: "''",
                            width: '5px',
                            height: '5px',
                            backgroundColor: '#666666',
                            borderRadius: '50%',
                            position: 'absolute',
                            left: '0',
                            top: '6px',
                        }
                    }
                },
            }
        },
        '&[x-placement="top-start"]': {
            margin: '0 auto 20px',
            '& .custom-tooltip': {
                '&::before': {
                    top: 'unset',
                    bottom: '-15px',
                    transform: 'rotate(180deg)',
                },
                '&::after': {
                    bottom: '-15px',
                    top: 'unset',
                    transform: 'rotate(180deg) scale(1.4)',
                }
            }
        },
    },
    limitExceed: {
        borderColor: "#EC1C24 !important",
        background: "#FFF3F3"
    },
    textRed: {
        color: "#EC1C24 !important",
    },
});

interface RejectionReasonModalProps extends WithStyles<typeof styles> {
    classes: any;
    isRejectionReasonModalOpen: boolean;
    handleRejectionReasonModalClose: Function;
    questionRejectionReasons: any[];
    onRejectionSubmit: Function;
}

interface RejectionReasonModalStates {
    selectedRejectionReasonIds: string[];
    feedbackText: string;
}

class RejectionReasonModal extends Component<RejectionReasonModalProps, RejectionReasonModalStates> {
    constructor(props: RejectionReasonModalProps) {
        super(props);
        this.state = {
            selectedRejectionReasonIds: [],
            feedbackText: '',
        };
    }

    handleReasonSelection = (event: any, reason: any) => {
        const { selectedRejectionReasonIds } = this.state;
        if(event.target.checked) {
            selectedRejectionReasonIds.push(reason._id);
        } else {
            const index = selectedRejectionReasonIds.indexOf(reason._id);
            if (index !== -1) {
                selectedRejectionReasonIds.splice(index, 1);
            }
        }
        this.setState({selectedRejectionReasonIds});
    }

    onRejectionSubmit = () => {
        const {selectedRejectionReasonIds, feedbackText} = this.state;
        const {questionRejectionReasons} = this.props;
        const rejectionReasons = questionRejectionReasons.filter((reason: any) =>
            selectedRejectionReasonIds.includes(reason._id)
        ).map((reason: any) => ({ id: reason._id, name: reason.name }));
        const rejectionComment = feedbackText;
        this.props.onRejectionSubmit({rejectionReasons, rejectionComment});
    }

    onModalClose = () => {
        this.props.handleRejectionReasonModalClose();
        this.setState({
            selectedRejectionReasonIds: [],
            feedbackText: '',
        });
    }

    render() {
        const { classes, isRejectionReasonModalOpen, handleRejectionReasonModalClose, questionRejectionReasons } = this.props;
        const { selectedRejectionReasonIds, feedbackText } = this.state;
        return (
            <MyPATDialog
                dialogHeading={
                    <span className={classes.dialogCustomHeading}>Reason(s) for Rejection</span>
                }
                dialogOpen={isRejectionReasonModalOpen}
                dialogHasActions={false}
                onDialogClose={this.onModalClose}
                customButtonVariant="withoutBorderCloseIcon"
                isAlignCenter={true}
                stopParentScroll={false}
                otherDialogProps={{
                    fullWidth: true,
                    maxWidth: 'sm',
                    scroll: 'body'
                }}
                customDialogActions={
                    <div className={classes.buttonSection}>
                        <Button
                            className={classes.primaryBtn}
                            autoFocus
                            variant="contained"
                            color="primary"
                            disabled={!selectedRejectionReasonIds.length || (selectedRejectionReasonIds.includes(OTHER_REASON_ID) && !feedbackText.length) || feedbackText.length > MAXIMUM_REVIEW_CHARACTERS}
                            onClick={this.onRejectionSubmit}
                        >
                            Submit
                        </Button>
                    </div>
                }
            >
                <div className={classes.rejectionReasonSection}>
                    <div className={classes.formSection}>
                        <div className='form-group'>
                            <label>Select the reason for rejection</label>
                            <div className={classes.rejectionList}>
                                {(questionRejectionReasons || []).map((rejectionReason: any) => <Tooltip
                                    classes={{
                                        popper: classes.customTooltipOuter,
                                        tooltip: 'custom-tooltip',
                                    }}
                                    placement="bottom-start"
                                    title={!!rejectionReason.references.length &&
                                        <div className='custom-tooltip-inner'>
                                            <ul className='listing'>
                                                {(rejectionReason.references || []).map((description: string, index: number) => <li key={index}>{description}</li>)}
                                            </ul>
                                        </div>
                                    }
                                >
                                    <div className='rejection-list-item' onClick={(event)=> this.handleReasonSelection(event, rejectionReason)} >{rejectionReason.name}<input type="checkbox" checked={(selectedRejectionReasonIds || []).some((selectedId: any) => selectedId === rejectionReason._id)} /></div>
                                </Tooltip>
                                )}
                            </div>
                        </div>
                        {/* <div className='form-group'>
                            <label>Specify reason for rejection</label>
                            <input type="text" placeholder='Please type here' />
                        </div> */}
                        <div className='form-group'>
                            <label>Please share overall feedback or any other remark <span className='light-text'>{!selectedRejectionReasonIds.includes(OTHER_REASON_ID) && <> (Optional)</>}</span></label>
                            <textarea placeholder='Please type here' className={cx({[classes.limitExceed]: feedbackText.length > MAXIMUM_REVIEW_CHARACTERS})} onChange={(event) => this.setState({feedbackText: event.target.value})} value={feedbackText}></textarea>
                            <em className={cx('note-text', {[classes.textRed]: feedbackText.length > MAXIMUM_REVIEW_CHARACTERS})}>{feedbackText.length > MAXIMUM_REVIEW_CHARACTERS ? CHARACTER_LIMIT.ERROR : CHARACTER_LIMIT.INFO}</em>
                        </div>
                    </div>
                </div>
            </MyPATDialog>
        )
    }
}

// @ts-ignore
export default (withStyles(styles)(RejectionReasonModal));
